import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import { type CallOptions, type Client } from "@connectrpc/connect";
import { BrandKitSchema, BrandsService, type BrandKit } from "#gen/brands/v1/service_pb.js";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import type { Filter } from "#lib/api/pagination.js";
import { PUBLIC_URLS } from "#lib/url.js";

export interface BrandKitList {
	brandKits: BrandKit[];
	pageInfo?: PaginationPageInfo;
}

export class BrandClient {
	private client: Client<typeof BrandsService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.brands || "/", BrandsService, options);
	}

	async getBrandKit(id: string): Promise<BrandKit> {
		try {
			const res = await this.client.getBrandKit({ id });
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getDefaultBrandKit(orgId: string, workspaceId?: string): Promise<BrandKit | undefined> {
		try {
			const res = await this.client.getDefaultBrandKit({ orgId, workspaceId });
			return res.brandKit;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listBrandKits(orgId: string, workspaceId: string, filter?: Filter, pagination?: PaginationArguments, options?: CallOptions): Promise<BrandKitList> {
		try {
			const res = await this.client.listBrandKits(
				{
					orgId,
					workspaceId,
					pagination,
					namePrefix: filter?.namePrefix,
					creatorIds: filter?.creators?.map(c => c.id),
					tags: filter?.tags,
					states: filter?.states,
					visibilities: filter?.visibilities,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateBrandKit(url: string): Promise<BrandKit> {
		try {
			const res = await this.client.generateBrandKit({ brandUrl: url });
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createBrandKit(brandKit: BrandKit): Promise<BrandKit> {
		try {
			const res = await this.client.createBrandKit({ brandKit });
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateBrandKit(brandKit: BrandKit, mask?: FieldMask): Promise<BrandKit> {
		try {
			const res = await this.client.updateBrandKit({
				brandKit,
				mask,
			});
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async setDefaultBrandKit(orgId: string, workspaceId: string | undefined, brandKit: BrandKit | undefined): Promise<void> {
		try {
			await this.client.setDefaultBrandKit({
				orgId,
				workspaceId,
				id: brandKit?.id,
			});
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveBrandKit(id: string): Promise<BrandKit> {
		try {
			const res = await this.client.updateBrandKit({
				brandKit: create(BrandKitSchema, {
					id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreBrandKit(id: string): Promise<BrandKit> {
		try {
			const res = await this.client.updateBrandKit({
				brandKit: create(BrandKitSchema, {
					id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.brandKit!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteBrandKit(id: string): Promise<void> {
		try {
			await this.client.deleteBrandKit({ id });
		} catch (e) {
			throw handleError(e);
		}
	}
}
